import { AppDispatch, createAppThunk, RootState } from '@mse-reg/app/store';
import { hasAnyRole, IAccountClient, IAuthClient, RoleType, UasDto, UserDto } from '@mse-reg/clients';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const initialState = {
  loading: false as boolean,
  isLoggedIn: false as boolean,
  account: null as UserDto | null,
  errorMessage: null as string | null,
  redirectMessage: null as string | null,
  sessionHasBeenFetched: false as boolean,
  idToken: null as string | null,
  logoutUrl: null as string | null,
  allowedUas: [] as UasDto[],
};

export type AuthState = typeof initialState;

const getSessionThunk = createAppThunk(
  'auth/getSession',
  async (_, deps: { accountClient: IAccountClient }) => {
    return await deps.accountClient.getCurrentUserInfo();
  },
  (r) => ({ accountClient: r.resolve<IAccountClient>('IAccountClient') }),
);

const refreshAllowedUasThunk = createAppThunk(
  'auth/refreshAllowedUas',
  async (_, deps: { accountClient: IAccountClient }) => {
    const currentUser = await deps.accountClient.getCurrentUserInfo();
    return currentUser?.allowedUas ?? [];
  },
  (r) => ({ accountClient: r.resolve<IAccountClient>('IAccountClient') }),
);

const logoutThunk = createAppThunk(
  'auth/logout',
  async (_, deps: { authClient: IAuthClient }, dispatch) => {
    const logoutResult = await deps.authClient.logOut();
    await dispatch(getSessionThunk()).unwrap();
    return ((logoutResult.data as any).logoutUrl ?? '') as string;
  },
  (r) => ({ authClient: r.resolve<IAuthClient>('IAuthClient') }),
);

export const isInRole =
  (...roleTypes: RoleType[]) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!getState().auth.isLoggedIn) {
      return false;
    }

    return hasAnyRole(getState().auth.account?.roles ?? [], roleTypes); // Return the computed result
  };

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAllowedUas(state, action: PayloadAction<UasDto[]>) {
      state.allowedUas = action.payload;
    },
    clearAuth(state) {
      state.loading = false;
      state.isLoggedIn = false;
      state.account = null;
      state.allowedUas = [];
    },
    errorMessage(state, action: PayloadAction<string>) {
      state.redirectMessage = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getSessionThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSessionThunk.fulfilled, (state, action: PayloadAction<UserDto>) => {
        state.loading = false;
        state.isLoggedIn = !!(action.payload?.firstName || action.payload?.lastName);
        state.sessionHasBeenFetched = true;
        state.account = action.payload;
        state.allowedUas = action.payload?.allowedUas || [];
      })
      .addCase(getSessionThunk.rejected, (state) => {
        state.loading = false;
        state.isLoggedIn = false;
        state.sessionHasBeenFetched = true;
        state.errorMessage = 'Failed to fetch user data';
      })
      .addCase(refreshAllowedUasThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(refreshAllowedUasThunk.fulfilled, (state, action: PayloadAction<UasDto[]>) => {
        state.loading = false;
        state.allowedUas = action.payload || [];
      })
      .addCase(refreshAllowedUasThunk.rejected, (state) => {
        state.loading = false;
        state.isLoggedIn = false;
        state.allowedUas = [];
      })
      .addCase(logoutThunk.fulfilled, (state, action: PayloadAction<string>) => {
        state.idToken = null;
        state.logoutUrl = action.payload;
      });
  },
});

export const authReducer = authSlice.reducer;
export const authActions = authSlice.actions;
export const authThunks = { getSessionThunk, refreshAllowedUasThunk, logoutThunk, isInRole };
